/**
 * Terms.tsx — صفحة الشروط والأحكام
 *
 * Companion legal page to /privacy. Same structural template (legal header,
 * TOC, sections, document footer, minimal site footer). Zero-JS, prerendered.
 */
import { siteConfig } from "@/config/siteConfig";

const itqanLogo = "/brand/itqan-logo.png";

const TOC = [
  { id: "definitions", label: "١. التعريفات" },
  { id: "acceptance", label: "٢. قبول الشروط" },
  { id: "services", label: "٣. وصف الخدمات" },
  { id: "eligibility", label: "٤. أهلية الاستخدام" },
  { id: "client-obligations", label: "٥. التزامات العميل" },
  { id: "our-obligations", label: "٦. التزاماتنا" },
  { id: "payment", label: "٧. الدفع والفواتير" },
  { id: "ip", label: "٨. الملكية الفكرية" },
  { id: "confidentiality", label: "٩. السرّية" },
  { id: "liability", label: "١٠. حدود المسؤولية" },
  { id: "indemnity", label: "١١. التعويض" },
  { id: "termination", label: "١٢. الإنهاء" },
  { id: "law", label: "١٣. القانون الواجب التطبيق" },
  { id: "disputes", label: "١٤. تسوية النزاعات" },
  { id: "contact", label: "١٥. التواصل" },
] as const;

const Terms = () => (
  <>
    <a href="#main" className="skip-to-content">تخطّي إلى المحتوى</a>

    <header className="legal-header" role="banner">
      <div className="legal-header-inner">
        <a href="/" className="brand" aria-label="إتقان — الصفحة الرئيسية">
          <img
            src={itqanLogo}
            alt=""
            width={40}
            height={40}
            className="brand-logo"
            fetchPriority="high"
            decoding="async"
          />
        </a>
        <a href="/" className="back-home">
          <span aria-hidden="true">→</span>
          العودة للرئيسية
        </a>
      </div>
    </header>

    <main id="main" className="legal-main" role="main">
      <article className="legal-document">

        <header className="legal-doc-header">
          <span className="legal-eyebrow">شروط قانونية · إتقان</span>
          <h1 className="legal-title">الشروط والأحكام</h1>
          <p className="legal-meta">
            آخر تحديث: ٢٥ أبريل ٢٠٢٦ · تاريخ السريان: ٢٥ أبريل ٢٠٢٦ · الإصدار ١٫٠
          </p>
        </header>

        <nav className="legal-toc" aria-label="فهرس المحتويات">
          <h2 className="legal-toc-title">المحتويات</h2>
          <ol>
            {TOC.map((t) => (
              <li key={t.id}><a href={`#${t.id}`}>{t.label}</a></li>
            ))}
          </ol>
        </nav>

        <section id="definitions" className="legal-section">
          <h2>١. التعريفات</h2>
          <ul>
            <li><strong>"إتقان" / "نحن":</strong> منصة إتقان للتجارة الإلكترونية والتسويق الرقمي، الكائنة في الرياض، المملكة العربية السعودية.</li>
            <li><strong>"العميل" / "أنت":</strong> أي شخص طبيعي أو اعتباري يتعاقد معنا للحصول على خدماتنا.</li>
            <li><strong>"الخدمات":</strong> جميع الخدمات التي تقدّمها إتقان، بما في ذلك بناء المتاجر الإلكترونية، إدارة الحملات الإعلانية، الاستشارات، التصميم، والتطوير.</li>
            <li><strong>"الاتفاقية":</strong> هذه الشروط والأحكام، إضافةً إلى أي عقد منفصل أو عرض سعر مُوقّع بيننا.</li>
          </ul>
        </section>

        <section id="acceptance" className="legal-section">
          <h2>٢. قبول الشروط</h2>
          <p>
            باستخدامك موقع <strong>itqan.agency</strong> أو التعاقد معنا للحصول على أي خدمة، فإنك توافق صراحةً على الالتزام بهذه الشروط والأحكام، وعلى سياسة الخصوصية المرفقة. إذا كنت لا توافق على أيّ بند، فيُرجى عدم استخدام خدماتنا.
          </p>
          <p>
            في حال وجود عقد منفصل بيننا، تكون أحكام ذلك العقد هي السارية في حالة التعارض، وتُعتبر هذه الشروط مكمّلة له.
          </p>
        </section>

        <section id="services" className="legal-section">
          <h2>٣. وصف الخدمات</h2>
          <p>تُقدّم إتقان الخدمات التالية على سبيل المثال لا الحصر:</p>
          <ul>
            <li>تصميم تجربة المستخدم وواجهات المستخدم.</li>
            <li>بناء وإدارة المتاجر الإلكترونية على منصات سلة، زد، شوبيفاي، وحلول مخصصة.</li>
            <li>إطلاق وإدارة الحملات الإعلانية على Google، Meta، TikTok، Snap، YouTube.</li>
            <li>تحسين معدلات التحويل (CRO) عبر اختبارات A/B مستمرة.</li>
            <li>تحسين محركات البحث (SEO) تقني ومحتوى.</li>
            <li>تسويق المؤثرين وإدارة محتوى السوشيال ميديا.</li>
            <li>الاستشارات الاستراتيجية الرقمية.</li>
          </ul>
          <p>نحتفظ بالحق في تطوير أو تعديل أو إيقاف أي خدمة، مع إشعار العملاء المعنيين مسبقًا.</p>
        </section>

        <section id="eligibility" className="legal-section">
          <h2>٤. أهلية الاستخدام</h2>
          <p>
            خدماتنا موجّهة للشركات وأصحاب الأعمال، ويُشترط أن تكون قد بلغت <strong>١٨ عامًا</strong> فأكثر، وتتمتع بالأهلية النظامية الكاملة للتعاقد. عند التعاقد بصفتك ممثلًا لشركة، فإنك تُقرّ بأنك مفوّض رسميًا بإلزامها.
          </p>
        </section>

        <section id="client-obligations" className="legal-section">
          <h2>٥. التزامات العميل</h2>
          <ul>
            <li>تزويدنا بكامل المعلومات والملفات الضرورية لتنفيذ الخدمة في المواعيد المتّفق عليها.</li>
            <li>الردّ على استفساراتنا وطلبات المراجعة خلال مدّة معقولة (لا تتجاوز ٧٢ ساعة عمل).</li>
            <li>سداد المبالغ المستحقة في مواعيدها.</li>
            <li>عدم استخدام خدماتنا لأي غرض غير مشروع، أو لنشر محتوى مخالف للأنظمة السعودية.</li>
            <li>احترام حقوق الملكية الفكرية للأطراف الثالثة، وعدم تكليفنا بأي عمل ينتهك هذه الحقوق.</li>
            <li>الحفاظ على سرّية بيانات الدخول والصلاحيات التي نمنحك إيّاها.</li>
          </ul>
        </section>

        <section id="our-obligations" className="legal-section">
          <h2>٦. التزاماتنا</h2>
          <ul>
            <li>تقديم الخدمات وفق المعايير المهنية المتعارف عليها.</li>
            <li>الالتزام بالجدول الزمني المتّفق عليه، مع إخطارك بأي تأخير محتمل وأسبابه.</li>
            <li>الحفاظ على سرّية كامل بياناتك ومعلومات مشروعك.</li>
            <li>تقديم تقارير دورية شفافة عن سير العمل وأرقام الأداء.</li>
            <li>تخصيص فريق محترف مدرّب لتنفيذ مشروعك.</li>
            <li>الردّ على استفساراتك خلال ساعات العمل الرسمية ({siteConfig.workingDays.start} – {siteConfig.workingDays.end}، من {siteConfig.workingHours.start} إلى {siteConfig.workingHours.end}).</li>
          </ul>
        </section>

        <section id="payment" className="legal-section">
          <h2>٧. الدفع والفواتير</h2>
          <ul>
            <li>تُحدَّد قيمة الخدمات في عرض السعر أو العقد المنفصل.</li>
            <li>تُسدَّد <strong>دفعة مقدمة لا تقلّ عن ٥٠٪</strong> من قيمة المشروع قبل بدء العمل، ما لم يُتفق على غير ذلك كتابيًا.</li>
            <li>تُسدَّد المتبقّي وفق الجدول الزمني المتّفق عليه (مراحل، شهري، أو عند التسليم النهائي).</li>
            <li>تخضع جميع المبالغ لضريبة القيمة المضافة (١٥٪) وفق الأنظمة السعودية.</li>
            <li>يحقّ لنا تعليق العمل في حال التأخر في السداد لأكثر من <strong>١٥ يومًا</strong> من تاريخ الاستحقاق، مع إشعار مسبق.</li>
            <li>الدفعات المُسدَّدة غير قابلة للاسترداد بعد بدء العمل، إلا في حال إخلالنا الجوهري بالتزاماتنا.</li>
          </ul>
        </section>

        <section id="ip" className="legal-section">
          <h2>٨. الملكية الفكرية</h2>
          <p>
            <strong>أ. ملكية المخرجات النهائية:</strong> بعد سداد كامل المبالغ، تنتقل ملكية المخرجات النهائية (التصاميم، الكود، المحتوى) إليك، باستثناء:
          </p>
          <ul>
            <li>الأدوات الداخلية، والمكوّنات، والأطر البرمجية المملوكة لنا أو لأطراف ثالثة.</li>
            <li>التراخيص الخارجية (خطوط، صور، إضافات) التي تخضع لشروط مالكيها.</li>
          </ul>
          <p>
            <strong>ب. علامتنا التجارية:</strong> اسم "إتقان"، الشعار، والمحتوى التسويقي مملوكة لنا حصريًا. لا يحقّ لك استخدامها دون إذن كتابي.
          </p>
          <p>
            <strong>ج. الإشارة إلينا:</strong> يحقّ لنا الإشارة إلى مشروعك ضمن أعمالنا (Portfolio) وحالات الدراسة، إلا إذا اتفقنا كتابيًا على خلاف ذلك.
          </p>
        </section>

        <section id="confidentiality" className="legal-section">
          <h2>٩. السرّية</h2>
          <p>
            يلتزم كلا الطرفين بالحفاظ على سرّية كل المعلومات والوثائق المتبادلة، وعدم الإفصاح عنها لأي طرف ثالث دون إذن كتابي مسبق، باستثناء:
          </p>
          <ul>
            <li>المعلومات المتاحة للعموم بصورة مشروعة.</li>
            <li>المعلومات الواجب الإفصاح عنها بموجب أمر قضائي أو طلب نظامي رسمي.</li>
            <li>المعلومات الضرورية لمزوّدي خدماتنا الذين يلتزمون باتفاقيات سرّية معنا.</li>
          </ul>
          <p>تستمر التزامات السرّية لمدّة <strong>٥ سنوات</strong> بعد انتهاء الاتفاقية.</p>
        </section>

        <section id="liability" className="legal-section">
          <h2>١٠. حدود المسؤولية</h2>
          <p>
            تُقدَّم خدماتنا "كما هي". لا نضمن نتائج تجارية محدّدة (مثل أرقام مبيعات أو معدلات تحويل بعينها)، إذ تتأثر بعوامل خارجة عن سيطرتنا (السوق، المنتج، التسعير، المنافسة).
          </p>
          <p>
            <strong>الحدّ الأقصى لمسؤوليتنا</strong> عن أي ضرر مباشر أو غير مباشر هو قيمة المبالغ المُسدَّدة فعلًا خلال <strong>الـ ٣ أشهر السابقة</strong> لتاريخ الحادثة المسبّبة للضرر.
          </p>
          <p>لا نتحمّل المسؤولية عن:</p>
          <ul>
            <li>الأضرار غير المباشرة، التبعية، أو فقدان الأرباح.</li>
            <li>أعطال المنصات الخارجية (سلة، زد، Google، Meta، إلخ).</li>
            <li>أي ضرر ناتج عن استخدامك المخالف لتعليماتنا أو لشروط المنصات.</li>
            <li>القوة القاهرة (كوارث طبيعية، انقطاع شامل للإنترنت، قرارات حكومية).</li>
          </ul>
        </section>

        <section id="indemnity" className="legal-section">
          <h2>١١. التعويض</h2>
          <p>
            توافق على تعويضنا والدفاع عنّا ضدّ أي مطالبات أو خسائر أو نفقات تنشأ عن:
          </p>
          <ul>
            <li>انتهاكك لهذه الشروط أو للأنظمة السارية.</li>
            <li>المحتوى الذي تزوّدنا به وانتهاكه لحقوق طرف ثالث.</li>
            <li>استخدامك المخالف للخدمات أو إساءة استخدامها.</li>
          </ul>
        </section>

        <section id="termination" className="legal-section">
          <h2>١٢. الإنهاء</h2>
          <p>يحقّ لكلا الطرفين إنهاء الاتفاقية في الحالات التالية:</p>
          <ul>
            <li><strong>الإنهاء بالتراضي:</strong> باتفاق كتابي بين الطرفين.</li>
            <li><strong>الإنهاء بالإشعار:</strong> بإشعار كتابي مسبق لا يقلّ عن <strong>٣٠ يومًا</strong>، مع تسوية كامل المستحقات حتى تاريخ الإنهاء.</li>
            <li><strong>الإنهاء بسبب الإخلال:</strong> فوريًا في حال إخلال أحد الطرفين إخلالًا جوهريًا بالتزاماته، ولم يُصلح الإخلال خلال ١٤ يومًا من إشعاره به.</li>
          </ul>
          <p>عند الإنهاء، تُسلَّم المخرجات المُنجزة حتى ذلك التاريخ مقابل تسوية المستحقات، ويُحذف ما يخصّ مشروعك من أنظمتنا وفق سياسة الاحتفاظ المُعلنة.</p>
        </section>

        <section id="law" className="legal-section">
          <h2>١٣. القانون الواجب التطبيق</h2>
          <p>
            تخضع هذه الاتفاقية وتُفسَّر وفق أنظمة <strong>المملكة العربية السعودية</strong>، بما فيها نظام التجارة الإلكترونية، نظام مكافحة الجرائم المعلوماتية، نظام حماية البيانات الشخصية، نظام الشركات، ونظام مكافحة التستر التجاري.
          </p>
        </section>

        <section id="disputes" className="legal-section">
          <h2>١٤. تسوية النزاعات</h2>
          <p>في حال نشوء أي نزاع، يلتزم الطرفان بمحاولة تسويته وُدّيًا خلال <strong>٣٠ يومًا</strong> من إشعار النزاع كتابيًا.</p>
          <p>
            إذا تعذّرت التسوية الودّية، يُحال النزاع إلى <strong>المحاكم المختصة في مدينة الرياض</strong>، المملكة العربية السعودية، صاحبة الاختصاص الحصري بالنظر فيه.
          </p>
        </section>

        <section id="contact" className="legal-section">
          <h2>١٥. التواصل</h2>
          <p>للاستفسار عن أي بند في هذه الشروط:</p>
          <dl className="legal-contact-list">
            <dt>البريد العام:</dt>
            <dd><a href={`mailto:${siteConfig.email}`}>{siteConfig.email}</a></dd>

            <dt>البريد القانوني:</dt>
            <dd><a href="mailto:legal@itqan.agency">legal@itqan.agency</a></dd>

            <dt>الهاتف وواتساب:</dt>
            <dd dir="ltr"><a href={`tel:${siteConfig.phoneRaw}`}>{siteConfig.phone}</a></dd>

            <dt>العنوان البريدي:</dt>
            <dd>إتقان، حي العليا، الرياض، المملكة العربية السعودية</dd>
          </dl>
        </section>

        <footer className="legal-doc-footer">
          <p className="legal-version">
            <span>الإصدار ١٫٠</span>
            <span className="legal-divider">·</span>
            <span>تاريخ السريان: ٢٥ أبريل ٢٠٢٦</span>
            <span className="legal-divider">·</span>
            <span>آخر تحديث: ٢٥ أبريل ٢٠٢٦</span>
          </p>
          <a href="/" className="back-home-bottom">العودة إلى الصفحة الرئيسية ←</a>
        </footer>

      </article>
    </main>

    <footer role="contentinfo" className="legal-site-footer">
      <div className="legal-footer-inner">
        <p className="legal-footer-brand">© {new Date().getFullYear()} إتقان · جميع الحقوق محفوظة</p>
        <nav className="legal-footer-nav" aria-label="روابط قانونية">
          <a href="/">الرئيسية</a>
          <span aria-hidden="true">·</span>
          <a href="/privacy">سياسة الخصوصية</a>
          <span aria-hidden="true">·</span>
          <a href="/terms">الشروط والأحكام</a>
          <span aria-hidden="true">·</span>
          <a href="/#contact">تواصل</a>
        </nav>
      </div>
    </footer>
  </>
);

export default Terms;
